import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UsageGauge } from '@/components/UsageGauge';
import { UsageBar } from '@/components/UsageBar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  HardDrive,
  Database,
  RefreshCw,
  FolderOpen,
  Building2,
  Table2,
  AlertTriangle,
  Loader2,
  Network,
  Activity,
  Info,
  Receipt,
  BadgeDollarSign,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatBytes, pct, share } from '@/lib/usage-utils';

// Supabase Free plan quotas (bytes)
const STORAGE_LIMIT = 1 * 1024 * 1024 * 1024; // 1 GB
const DB_LIMIT = 500 * 1024 * 1024; // 500 MB
const EGRESS_LIMIT = 5 * 1024 * 1024 * 1024; // 5 GB

interface PlanUsage {
  storage_bytes: number;
  storage_by_bucket: { bucket: string; bytes: number }[];
  storage_by_gym: { gym_id: string; bytes: number }[];
  db_size_bytes: number;
  rows_by_table: Record<string, number>;
}

interface PlanMetrics {
  success: boolean;
  reason?: string;
  project_ref?: string;
  period?: { from: string | null; to: string | null };
  invocations?: {
    total: number;
    rest: number;
    auth: number;
    storage: number;
    realtime: number;
  };
  egress_bytes?: number | null;
  egress_by_service?: Record<string, number>;
  egress_available?: boolean;
}

interface GymUsage {
  gym_id: string;
  storage_bytes: number;
  db_bytes: number;
  total_rows: number;
  rows_by_table: Record<string, number>;
}

interface GymMetrics {
  success: boolean;
  reason?: string;
  gym_id?: string;
  window?: string;
  approximate?: boolean;
  requests?: number;
  note?: string;
}

interface FacturacionSummary {
  gym_id?: string;
  total_documents?: number;
  processed_documents?: number;
  draft_documents?: number;
  rejected_documents?: number;
  processed_amount?: number;
}

export default function UsagePage() {
  const { hasRole } = useAuth();
  const [lastRefetched, setLastRefetched] = useState<string>('');
  const [selectedGymId, setSelectedGymId] = useState<string>('');

  const isSuperAdmin = hasRole(['super_admin']);

  const { data: usage, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['planUsage'],
    queryFn: async () => {
      const res = await adminApi.getPlanUsage();
      setLastRefetched(new Date().toLocaleTimeString('es-ES'));
      return res.usage as PlanUsage;
    },
    enabled: isSuperAdmin,
    refetchInterval: 30000,
    retry: false,
  });

  const { data: metrics, isFetching: isFetchingMetrics, refetch: refetchMetrics } = useQuery({
    queryKey: ['planUsageMetrics'],
    queryFn: async () => {
      const res = await adminApi.getPlanUsageMetrics();
      return res.metrics as PlanMetrics;
    },
    enabled: isSuperAdmin,
    // The Management API is rate limited (30 req/min) and the Edge Function
    // caches for 60s — no need to poll aggressively.
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
    retry: false,
  });

  const { data: gyms = [] } = useQuery({
    queryKey: ['gymList'],
    queryFn: async () => {
      const res = await adminApi.listGyms();
      return res.gyms;
    },
    enabled: isSuperAdmin,
    staleTime: 5 * 60 * 1000,
  });

  // Per-gym consumption (only attributable params: storage, rows/bytes, REST requests)
  const {
    data: gymUsage,
    isLoading: isLoadingGymUsage,
    isFetching: isFetchingGymUsage,
    refetch: refetchGymUsage,
  } = useQuery({
    queryKey: ['gymUsage', selectedGymId],
    queryFn: async () => {
      const res = await adminApi.getGymUsage(selectedGymId);
      return res.usage as GymUsage;
    },
    enabled: isSuperAdmin && !!selectedGymId,
    staleTime: 60 * 1000,
    retry: false,
  });

  const {
    data: gymMetrics,
    isFetching: isFetchingGymMetrics,
    refetch: refetchGymMetrics,
  } = useQuery({
    queryKey: ['gymUsageMetrics', selectedGymId],
    queryFn: async () => {
      const res = await adminApi.getGymUsageMetrics(selectedGymId);
      return res.metrics as GymMetrics;
    },
    enabled: isSuperAdmin && !!selectedGymId,
    staleTime: 60 * 1000,
    retry: false,
  });

  const {
    data: facturacionSummary,
    isLoading: isLoadingFacturacion,
    isFetching: isFetchingFacturacion,
    refetch: refetchFacturacion,
  } = useQuery({
    queryKey: ['gymFacturacionSummary', selectedGymId],
    queryFn: async () => {
      const res = await adminApi.getGymFacturacionSummary(selectedGymId);
      return res.summary as FacturacionSummary;
    },
    enabled: isSuperAdmin && !!selectedGymId,
    staleTime: 60 * 1000,
    retry: false,
  });

  // Default the selector to the first gym once the list is loaded.
  useEffect(() => {
    if (!selectedGymId && gyms.length > 0) setSelectedGymId(gyms[0].gym_id);
  }, [gyms, selectedGymId]);

  const gymNameById = Object.fromEntries(gyms.map((g: any) => [g.gym_id, g.name]));

  if (!isSuperAdmin) {
    return (
      <DashboardLayout title="Uso del Plan">
        <div className="flex items-center justify-center h-[60vh] text-muted-foreground">
          No tienes acceso a esta sección.
        </div>
      </DashboardLayout>
    );
  }

  const storageUsed = usage?.storage_bytes ?? 0;
  const dbUsed = usage?.db_size_bytes ?? 0;
  const storagePct = pct(storageUsed, STORAGE_LIMIT);
  const dbPct = pct(dbUsed, DB_LIMIT);

  const egressUsed = metrics?.egress_bytes ?? 0;
  const egressPct = pct(egressUsed, EGRESS_LIMIT);
  const inv = metrics?.invocations;
  const metricsNotConfigured = metrics?.success === false && metrics?.reason === 'not_configured';
  const gymStorageShare = share(gymUsage?.storage_bytes ?? 0, storageUsed);
  const projectRows = Object.values(usage?.rows_by_table ?? {}).reduce(
    (acc, n) => acc + (n as number),
    0,
  );
  const gymRowsShare = share(gymUsage?.total_rows ?? 0, projectRows);
  const gymRequestsShare = share(gymMetrics?.requests ?? 0, inv?.rest ?? 0);
  const gymMetricsUnavailable = !!selectedGymId && gymMetrics?.success === false;
  const periodLabel = metrics?.period?.from
    ? `${new Date(metrics.period.from).toLocaleDateString('es-ES')} → ${
        metrics.period.to ? new Date(metrics.period.to).toLocaleDateString('es-ES') : 'hoy'
      }`
    : 'últimas 24 h';

  return (
    <DashboardLayout
      title="Uso del Plan"
      subtitle="Monitoreo interno del consumo de Supabase (solo super admin)"
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            refetch();
            refetchMetrics();
            if (selectedGymId) {
              refetchGymUsage();
              refetchGymMetrics();
              refetchFacturacion();
            }
          }}
          disabled={isFetching || isFetchingMetrics || isFetchingGymUsage || isFetchingGymMetrics || isFetchingFacturacion}
        >
          {isFetching || isFetchingMetrics || isFetchingGymUsage || isFetchingGymMetrics || isFetchingFacturacion ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Actualizar
        </Button>
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-6">
          {lastRefetched && (
            <p className="text-xs text-muted-foreground">Última actualización: {lastRefetched} (auto-refresh 30s)</p>
          )}

          {/* Plan-capacity gauges (metrics with a real plan limit) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <HardDrive className="h-5 w-5 text-primary" />
                  Almacenamiento (Storage)
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <UsageGauge
                  value={storagePct}
                  usedLabel={formatBytes(storageUsed)}
                  totalLabel={formatBytes(STORAGE_LIMIT)}
                />
                {storagePct >= 80 && (
                  <p className="mt-3 flex items-center gap-1.5 text-xs text-destructive font-bold">
                    <AlertTriangle className="h-4 w-4" /> Se acerca al límite del plan Free
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Database className="h-5 w-5 text-primary" />
                  Base de Datos
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <UsageGauge
                  value={dbPct}
                  usedLabel={formatBytes(dbUsed)}
                  totalLabel={formatBytes(DB_LIMIT)}
                />
                {dbPct >= 80 && (
                  <p className="mt-3 flex items-center gap-1.5 text-xs text-destructive font-bold">
                    <AlertTriangle className="h-4 w-4" /> Base de datos cerca del límite
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Network className="h-5 w-5 text-primary" />
                  Egress (transferencia)
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                {metricsNotConfigured ? (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Info className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      No disponible. Configura el secreto{' '}
                      <code className="font-mono text-xs">MANAGEMENT_API_KEY</code> (token con
                      permiso <code className="font-mono text-xs">analytics_usage_read</code>) para leer
                      las métricas de la Management API.
                    </span>
                  </div>
                ) : !metrics?.egress_available ? (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Info className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      El egress facturado no se expone en la API pública por proyecto de Supabase (solo en
                      el dashboard de facturación de la organización).
                    </span>
                  </div>
                ) : (
                  <>
                    <UsageGauge
                      value={egressPct}
                      usedLabel={formatBytes(egressUsed)}
                      totalLabel={formatBytes(EGRESS_LIMIT)}
                    />
                    {egressPct >= 80 && (
                      <p className="mt-3 flex items-center gap-1.5 text-xs text-destructive font-bold">
                        <AlertTriangle className="h-4 w-4" /> Egress cerca del límite del plan Free
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* API requests (distribution across services) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                Invocaciones (solicitudes API)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {metricsNotConfigured ? (
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Info className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>No disponible hasta configurar el token de la Management API.</span>
                </div>
              ) : (
                <>
                  <div className="flex items-end justify-between mb-4">
                    <p className="text-3xl font-black">{(inv?.total ?? 0).toLocaleString('es-ES')}</p>
                    <p className="text-sm text-muted-foreground">{periodLabel}</p>
                  </div>
                  <div className="space-y-4">
                    {[
                      { label: 'REST (PostgREST)', value: inv?.rest ?? 0 },
                      { label: 'Auth', value: inv?.auth ?? 0 },
                      { label: 'Storage', value: inv?.storage ?? 0 },
                      { label: 'Realtime', value: inv?.realtime ?? 0 },
                    ].map((row) => (
                      <UsageBar
                        key={row.label}
                        value={share(row.value, inv?.total ?? 0)}
                        label={row.label}
                        valueLabel={row.value.toLocaleString('es-ES')}
                        tone="primary"
                      />
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">
                    Solicitudes REST/Auth/Storage/Realtime. Las invocaciones de Edge Functions no están
                    incluidas.
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Per-gym consumption (only attributable params) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Consumo por Gimnasio
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="text-sm text-muted-foreground">Gimnasio:</span>
                <Select value={selectedGymId} onValueChange={setSelectedGymId}>
                  <SelectTrigger className="w-full sm:w-[280px]">
                    <SelectValue placeholder="Elegir gimnasio..." />
                  </SelectTrigger>
                  <SelectContent>
                    {gyms.map((g: any) => (
                      <SelectItem key={g.gym_id} value={g.gym_id}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!selectedGymId ? (
                <p className="text-sm text-muted-foreground">Elige un gimnasio para ver su consumo.</p>
              ) : isLoadingGymUsage ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <HardDrive className="h-4 w-4" /> Almacenamiento
                      </div>
                      <p className="mt-2 text-2xl font-black">{formatBytes(gymUsage?.storage_bytes ?? 0)}</p>
                      <UsageBar
                        className="mt-3"
                        size="sm"
                        tone="primary"
                        value={gymStorageShare}
                        valueLabel={`${gymStorageShare}%`}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">del total del proyecto</p>
                    </div>
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Database className="h-4 w-4" /> Base de Datos
                      </div>
                      <p className="mt-2 text-2xl font-black">
                        {(gymUsage?.total_rows ?? 0).toLocaleString('es-ES')}{' '}
                        <span className="text-sm font-normal text-muted-foreground">filas</span>
                      </p>
                      <UsageBar
                        className="mt-3"
                        size="sm"
                        tone="primary"
                        value={gymRowsShare}
                        valueLabel={`${gymRowsShare}%`}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        del total · ≈ {formatBytes(gymUsage?.db_bytes ?? 0)} (estimado)
                      </p>
                    </div>
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Activity className="h-4 w-4" /> Invocaciones (24 h)
                      </div>
                      {gymMetricsUnavailable ? (
                        <p className="mt-2 text-sm text-muted-foreground">
                          No disponible{gymMetrics?.note ? ` — ${gymMetrics.note}` : ''}
                        </p>
                      ) : (
                        <>
                          <p className="mt-2 text-2xl font-black">
                            {(gymMetrics?.requests ?? 0).toLocaleString('es-ES')}{' '}
                            <span className="text-sm font-normal text-muted-foreground">≈</span>
                          </p>
                          <UsageBar
                            className="mt-3"
                            size="sm"
                            tone="primary"
                            value={gymRequestsShare}
                            valueLabel={`${gymRequestsShare}%`}
                          />
                          <p className="mt-1 text-xs text-muted-foreground">del REST del proyecto</p>
                        </>
                      )}
                    </div>
                  </div>

                  {Object.keys(gymUsage?.rows_by_table ?? {}).length > 0 && (
                    <div>
                      <p className="mb-2 text-sm font-medium">Filas por tabla</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {Object.entries(gymUsage?.rows_by_table ?? {})
                          .sort((a, b) => (b[1] as number) - (a[1] as number))
                          .map(([table, count]) => (
                            <div
                              key={table}
                              className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
                            >
                              <span className="text-sm font-medium truncate mr-2">{table}</span>
                              <Badge variant="secondary" className="font-mono shrink-0">
                                {(count as number).toLocaleString('es-ES')}
                              </Badge>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              <p className="text-xs text-muted-foreground">
                Consumo atribuible por gimnasio. El <strong>egress no se puede atribuir por gimnasio</strong>{' '}
                (Supabase no expone bytes de egress por proyecto/gym), por eso se muestra solo a nivel de
                proyecto. Las invocaciones son aproximadas: solo REST cuya consulta incluye{' '}
                <code className="font-mono">gym_id</code> (excluye auth, realtime y flujos públicos).
              </p>
            </CardContent>
          </Card>

          {/* Facturación electrónica por gimnasio */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-primary" />
                Facturación Electrónica por Gimnasio
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedGymId ? (
                <p className="text-sm text-muted-foreground">Elige un gimnasio para ver su consumo de DTE.</p>
              ) : isLoadingFacturacion ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Receipt className="h-4 w-4" /> Emitidos
                      </div>
                      <p className="mt-2 text-2xl font-black">
                        {(facturacionSummary?.total_documents ?? 0).toLocaleString('es-ES')}
                      </p>
                    </div>
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Activity className="h-4 w-4" /> Procesados
                      </div>
                      <p className="mt-2 text-2xl font-black text-emerald-600 dark:text-emerald-400">
                        {(facturacionSummary?.processed_documents ?? 0).toLocaleString('es-ES')}
                      </p>
                    </div>
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Info className="h-4 w-4" /> Borradores
                      </div>
                      <p className="mt-2 text-2xl font-black">
                        {(facturacionSummary?.draft_documents ?? 0).toLocaleString('es-ES')}
                      </p>
                    </div>
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <AlertTriangle className="h-4 w-4" /> Rechazados
                      </div>
                      <p className="mt-2 text-2xl font-black text-red-600 dark:text-red-400">
                        {(facturacionSummary?.rejected_documents ?? 0).toLocaleString('es-ES')}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 rounded-lg bg-muted/50 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <BadgeDollarSign className="h-4 w-4" /> Monto procesado (DTE transmitidos)
                    </div>
                    <p className="text-xl font-black">
                      ${(Number(facturacionSummary?.processed_amount ?? 0)).toLocaleString('es-SV', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Storage by bucket */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-primary" />
                Almacenamiento por Bucket
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(usage?.storage_by_bucket ?? []).map((b) => (
                  <UsageBar
                    key={b.bucket}
                    value={share(b.bytes, storageUsed)}
                    label={b.bucket}
                    valueLabel={formatBytes(b.bytes)}
                    tone="primary"
                  />
                ))}
                {(usage?.storage_by_bucket ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground">Sin archivos almacenados.</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Storage by gym (top consumers) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Consumo de Fotos por Gimnasio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(usage?.storage_by_gym ?? []).map((g) => (
                  <UsageBar
                    key={g.gym_id}
                    value={share(g.bytes, storageUsed)}
                    label={gymNameById[g.gym_id] || g.gym_id?.substring(0, 8) || 'Desconocido'}
                    valueLabel={formatBytes(g.bytes)}
                    tone="primary"
                  />
                ))}
                {(usage?.storage_by_gym ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground">Aún no hay fotos de clientes.</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Rows by table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Table2 className="h-5 w-5 text-primary" />
                Filas por Tabla
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(usage?.rows_by_table ?? {})
                  .sort((a, b) => (b[1] as number) - (a[1] as number))
                  .map(([table, count]) => (
                    <div key={table} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span className="text-sm font-medium truncate mr-2">{table}</span>
                      <Badge variant="secondary" className="font-mono shrink-0">{(count as number).toLocaleString('es-ES')}</Badge>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>

          <p className={cn("text-xs text-muted-foreground")}>
            Límites del plan Free: 1 GB Storage · 500 MB Database · 5 GB Egress · 50k MAU · 500k Edge
            Invocations. El Egress y las invocaciones se leen de la Management API vía la Edge Function
            <code className="font-mono mx-1">plan-usage-metrics</code>.
          </p>
        </div>
      )}
    </DashboardLayout>
  );
}
